import {
  AfterViewInit,
  Component,
  computed,
  inject,
  ViewChild,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import debounce from 'lodash/debounce';

import { type BatchComplianceRequest } from '#backend/entity/loaded/compliance/BatchComplianceRequest';
import {
  DatasafraSearchComponent,
  SearchParams,
} from '../../shared/components/datasafra-search/datasafra-search.component';
import {
  RequestsTableComponent,
  RequestTableAction,
  RequestTableConfig,
  RequestTableItem,
} from '../../shared/components/requests-table/requests-table.component';
import { isAbortOrAbortCause } from '../../shared/utils/is-abort-or-abort-cause';
import { SmartOrpcClientService } from '../../shared/services/smart-orpc-client.service';
import { TypeSafeMatCellDef } from '../../type-safe-mat-cell-def.directive';
import { UserService } from '../../shared/services/user.service';
import { environment } from '../../../environments/environment.local';
import { ELEMENT_DATA } from './mockData';
import { createNestedOrder } from '../../shared/utils/create-nested-order';
import { UserRole } from '#backend/common/user-types';
import { SnackbarService } from '../../shared/services/snackbar.service';
import { CommonModule } from '@angular/common';

type StatusCode = BatchComplianceRequest['status']['code'];

export interface ComplianceRequest extends RequestTableItem {
  organizationName?: string | null;
  userName?: string | null;
  requestedAndExecutedReportsCount?: number;
}

@Component({
  selector: 'app-compliances',
  viewProviders: [TypeSafeMatCellDef],
  imports: [
    DatasafraSearchComponent,
    DatePipe,
    FormsModule,
    MatButtonModule,
    MatCardModule,
    MatChipsModule,
    MatDatepickerModule,
    MatDividerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatMenuModule,
    MatPaginatorModule,
    MatProgressBarModule,
    MatSortModule,
    MatTableModule,
    MatTooltipModule,
    ReactiveFormsModule,
    RequestsTableComponent,
    CommonModule,
  ],
  templateUrl: './compliances.component.html',
  styleUrl: './compliances.component.scss',
  providers: [provideNativeDateAdapter()],
})
export class CompliancesComponent implements AfterViewInit {
  asTypedItem = (x: any) => x as RequestTableItem;
  @ViewChild(DatasafraSearchComponent)
  declare search: DatasafraSearchComponent;
  @ViewChild(RequestsTableComponent)
  declare requestTable: RequestsTableComponent;

  private readonly smartOrpcClientService = inject(SmartOrpcClientService);
  readonly userService = inject(UserService);
  private readonly snackBar = inject(SnackbarService);
  private readonly router = inject(Router);

  private _isLoading = false;

  showMockup = false;

  readonly dataSource = new MatTableDataSource<ComplianceRequest>([]);

  private abortLoad: AbortController | undefined;

  private expandedElement!: ComplianceRequest | null;

  // Armazena os últimos parâmetros de busca para manter estado na paginação
  private lastSearchParams: SearchParams = {};

  length = 0;

  tableConfig: RequestTableConfig = {
    showExpandColumn: true,
    showActionsColumn: true,
    showViewAdminColumn: false,
    columns: [
      'id',
      ...(this.userService.isGeoambiente ? ['cliente'] : []),
      'createdBy',
      'createdDate',
      'status',
    ],
    statusClassFunction: (statusCode: StatusCode) =>
      this.getStatusClass(statusCode),
    initialSort: { active: 'createdDate', direction: 'desc' },
  };

  // Ações da tabela
  tableActions: RequestTableAction[] = [
    {
      label: 'Acessar Relatórios',
      icon: 'description',
      action: (item) => this.goToComplianceReportsPage(item),

      condition: (item) => item.status.code === 'COMPLETED',
    },
  ];

  get isLoading(): boolean {
    return this._isLoading;
  }

  set isLoading(value: boolean) {
    this._isLoading = value;
  }
  navigateToCreationPage(): void {
    this.router.navigate(['/compliances/new']);
  }

  ngAfterViewInit() {
    if (environment.production || this.showMockup === false)
      this.debouncedLoadResponsive({});
    else this.dataSource.data = ELEMENT_DATA;
  }

  handleSearch(params: SearchParams) {
    this.lastSearchParams = params; // Armazena os parâmetros
    this.requestTable.paginator.firstPage();
    this.load(params);
  }

  handlePage() {
    this.debouncedLoadResponsive(this.lastSearchParams); // Usa os parâmetros armazenados
  }

  handleSort() {
    this.debouncedLoadResponsive(this.lastSearchParams); // Usa os parâmetros armazenados
  }

  debouncedLoadResponsive = debounce((v: SearchParams) => this.load(v), 500, {
    leading: true,
  });

  load(params: SearchParams) {
    this.abortLoad?.abort();
    this.abortLoad = new AbortController();
    this.isLoading = true;
    this.smartOrpcClientService.client.socEnvAnalysis
      .list(
        {
          cursor:
            this.requestTable.paginator.pageIndex *
            this.requestTable.paginator.pageSize,
          limit: this.requestTable.paginator.pageSize,
          textSearch: params.searchText ?? undefined,
          dateRange: {
            from: params.range?.start ?? undefined,
            to: params.range?.end ?? undefined,
          },
          onlyMine: params.onlyMine ?? true, // Adiciona o filtro onlyMine
          orderBy: this.dataSource.sort?.active
            ? createNestedOrder(this.dataSource.sort)
            : undefined,
        },
        { signal: this.abortLoad.signal },
      )
      .then(([complianceRequests, dataSize]) => {
        this.dataSource.data = complianceRequests as ComplianceRequest[];
        this.length = dataSize;
        this.isLoading = false;
      })
      .catch((err: unknown) => {
        if (!isAbortOrAbortCause(err)) {
          this.isLoading = false;
        }
      });
  }

  getStatusClass(status: StatusCode): string {
    switch (status) {
      case 'VALIDATING_INPUT':
      case 'ANALYZING':
        return 'chip-validando';
      case 'COMPLETED':
        return 'chip-concluida';
      case 'FAILED':
        return 'chip-cancelada';
      default:
        return '';
    }
  }

  canCreateRequest = computed(() => {
    const user = this.userService.user();
    if (!user) return false;
    return [
      UserRole.ADMIN_CLIENTE,
      UserRole.OPERADOR_GEOAMBIENTE,
      UserRole.ADMIN_GEOAMBIENTE,
    ].includes(user.role);
  });

  goToComplianceReportsPage(element: ComplianceRequest) {
    this.router.navigate(['compliances', element.id, 'reports']);
  }

  isExpanded(element: ComplianceRequest) {
    return this.expandedElement === element;
  }

  toggle(element: ComplianceRequest) {
    this.expandedElement = this.isExpanded(element) ? null : element;
  }

  getExtensao(fileName: string | null): string {
    if (!fileName) return '';
    return fileName.split('.').pop()?.toLowerCase() || '';
  }
}
